import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import { useMemo, useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"
import { Col, Row, Form, FormFeedback, Label, Button } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import VehicleIdSelect from "../../../components/Common/VehicleIdSelect"
import {
  useCreateVehicleMovement,
  useEditVehicleMovement,
} from "hooks/useVehicleMovements"
import FormikCreatableField from "components/Common/FormikCreatableField"

const VehicleMovementAddEdit = ({
  modal,
  toggleModal,
  isEdit,
  movement,
  optionLoading,
  movementTypesData,
  employeesData,
  locationsData,
}) => {
  const { mutateAsync: createMovement, isPending: createLoading } =
    useCreateVehicleMovement()
  const { mutateAsync: editMovement, isPending: editLoading } =
    useEditVehicleMovement()
  // Formik validation
  const validationSchema = Yup.object({
    vehicle_id: Yup.mixed().required("Please select vehicle"),
    movement_type_id: Yup.number().required("Please select movement type"),
    movement_by_id: Yup.number().required("Please select movement by"),
    location_from_id: Yup.number().required("Please select location from"),
    location_to_id: Yup.number().required("Please select location to"),
    movement_date: Yup.date().required("Please select movement date"),
    mileage: Yup.number()
      .required("Please enter mileage")
      .min(0, "Mileage must be >= 0"),
    remarks: Yup.string().nullable(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        ...values,
        vehicle_id: values.vehicle_id?.value || values.vehicle_id,
      }

      const action = isEdit
        ? editMovement({ id: movement.id, ...payload })
        : createMovement(payload)

      try {
        const res = await action
        toast.success(
          res?.message ??
            `Vehicle movement ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch (error) {
        console.error(error)
        toast.error("Failed to save vehicle movement.")
      }
    },
    [isEdit, movement, editMovement, createMovement, toggleModal]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      vehicle_id: movement?.vehicle?.id
        ? {
            label: `${movement.vehicle.stock_id} - ${
              movement.vehicle.full_chassis ?? ""
            }`,
            value: movement.vehicle.id,
          }
        : null,
      movement_type_id: movement?.movement_type_id || "",
      movement_by_id: movement?.movement_by_id || "",
      location_from_id: movement?.location_from_id || "",
      location_to_id: movement?.location_to_id || "",
      movement_date: movement?.movement_date || "",
      mileage: movement?.mileage || "",
      remarks: movement?.remarks || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  // ✅ Set vehicle_id field when editing
  useEffect(() => {
    if (movement?.vehicle?.id) {
      formik.setFieldValue("vehicle_id", movement.vehicle.id)
    }
  }, [movement])

  // Memoize button label and state
  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Vehicle Movement"}${
      loading ? "..." : ""
    }`
  }, [isEdit, editLoading, createLoading])

  const isSubmitDisabled = useMemo(
    () => optionLoading || editLoading || createLoading,
    [optionLoading, editLoading, createLoading]
  )

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Vehicle Movement" : "Add Vehicle Movement"}
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <Row className="mb-3">
          <Col sm={12} className="mb-3">
            <VehicleIdSelect formik={formik} />
          </Col>

          <Col sm={12}>
            <FormikCreatableField
              name="movement_type_id"
              label="Movement Type"
              formik={formik}
              data={movementTypesData}
              placeholder="Select Type"
            />
            {formik.touched.movement_type_id &&
              formik.errors.movement_type_id && (
                <FormFeedback type="invalid">
                  {formik.errors.movement_type_id}
                </FormFeedback>
              )}
          </Col>

          <Col sm={12} className="mb-3">
            <FormikCreatableField
              name="movement_by_id"
              label="Movement By"
              formik={formik}
              data={employeesData}
              placeholder="Select a staff"
            />
            {formik.touched.movement_by_id && formik.errors.movement_by_id && (
              <FormFeedback type="invalid">
                {formik.errors.movement_by_id}
              </FormFeedback>
            )}
          </Col>

          <Col sm={6} className="mb-3">
            <FormikCreatableField
              name="location_from_id"
              label="Location From"
              formik={formik}
              data={locationsData}
              placeholder="Select From"
            />
            {formik.touched.location_from_id &&
              formik.errors.location_from_id && (
                <FormFeedback type="invalid">
                  {formik.errors.location_from_id}
                </FormFeedback>
              )}
          </Col>

          <Col sm={6} className="mb-3">
            <FormikCreatableField
              name="location_to_id"
              label="Location To"
              formik={formik}
              data={locationsData}
              placeholder="Select To"
            />
            {formik.touched.location_to_id && formik.errors.location_to_id && (
              <FormFeedback type="invalid">
                {formik.errors.location_to_id}
              </FormFeedback>
            )}
          </Col>

          <Col sm={6} className="mb-3">
            <Label htmlFor="movement_date">Movement Date</Label>
            <InputField
              type="datetime-local"
              name="movement_date"
              id="movement_date"
              value={formik.values.movement_date}
              onChange={formik.handleChange}
              invalid={
                formik.touched.movement_date && !!formik.errors.movement_date
              }
              loading={optionLoading}
            />
            {formik.touched.movement_date && formik.errors.movement_date && (
              <FormFeedback type="invalid">
                {formik.errors.movement_date}
              </FormFeedback>
            )}
          </Col>

          <Col sm={6} className="mb-3">
            <Label htmlFor="mileage">Mileage</Label>
            <InputField
              type="number"
              name="mileage"
              id="mileage"
              value={formik.values.mileage}
              onChange={formik.handleChange}
              invalid={formik.touched.mileage && !!formik.errors.mileage}
              loading={optionLoading}
            />
            {formik.touched.mileage && formik.errors.mileage && (
              <FormFeedback type="invalid">
                {formik.errors.mileage}
              </FormFeedback>
            )}
          </Col>
        </Row>

        <div className="mb-3">
          <Label htmlFor="remarks">Remarks</Label>
          <InputField
            type="textarea"
            name="remarks"
            id="remarks"
            value={formik.values.remarks}
            onChange={formik.handleChange}
            invalid={formik.touched.remarks && !!formik.errors.remarks}
            loading={optionLoading}
          />
          {formik.touched.remarks && formik.errors.remarks && (
            <FormFeedback type="invalid">{formik.errors.remarks}</FormFeedback>
          )}
        </div>

        <div className="text-end">
          <Button color="success" type="submit" disabled={isSubmitDisabled}>
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default VehicleMovementAddEdit
